import type { VideoSelection } from "@unbox/api";
import { CursorBox, InfoCircle } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Focusable } from "react-aria-components";
import { useForm } from "react-hook-form";
import { useLogAction, useTakeAction, useUnreviewedCount } from "@/api/assurance/actions";
import { useVideoCollaborations } from "@/api/collaborations";
import { useStoreComposerImage } from "@/api/message-images";
import { useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { MessageCreatorFormFields } from "@/components/assurance/action-forms/message-creator-form-fields";
import { NotesFormFields } from "@/components/assurance/action-forms/notes-form-fields";
import { RemoveFromAdCampaignFormFields } from "@/components/assurance/action-forms/remove-from-ad-campaign-form-fields";
import { RemoveFromCollaborationFormFields } from "@/components/assurance/action-forms/remove-from-collaboration-form-fields";
import { TicketFormFields } from "@/components/assurance/action-forms/ticket-form-fields";
import { TriggerPolicyFormFields } from "@/components/assurance/action-forms/trigger-policy-form-fields";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Transcript } from "@/components/icons/transcript";
import { type ComposerImage, releaseComposerImage } from "@/components/message-composer/composer-image";
import { toastImageUploadFailed } from "@/components/message-composer/composer-toasts";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogClose, DialogFooter, DialogHeader, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { ActionCategory } from "@/lib/assurance/action-types";
import {
  type ActionType,
  ADS_NOT_CONNECTED_TOOLTIP,
  type ApiTakeActionType,
  getActionTypeIcon,
  getLogActionLabel,
  getTakeActionLabel,
  LOG_ACTION_TYPES,
  type LogActionType,
  MANUAL_TAKE_ACTION_TYPES,
  type UiTakeActionType,
} from "@/lib/assurance/action-types";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { success, warning } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";
import { SecurityInfoBox } from "../message-creator-dialogs";

type Video = {
  videoId: string;
  creatorId: string | null;
  thumbnail: string | null;
  productList?: string[];
};

export type ActionRecordDialogProps = {
  /** Videos for display purposes (thumbnails, unique creators, etc.) */
  videos: Video[];
  /** Video selection for API calls. When omitted, auto-creates include-mode from videos. */
  selection?: VideoSelection;
  /** Total selected count for display when using exclude-mode selection. Falls back to videos.length. */
  selectedCount?: number;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Pre-selected action type */
  preselectedAction?: ActionType;
  /** Pre-selected category (take/log) */
  preselectedCategory?: ActionCategory;
};

type ActionFormValues = {
  actionType: string;
  notes: string;
  message: string;
  groupRequests: string;
  policyId: string;
};

// Helper to get button text
const getButtonText = (
  actionType: ActionType | undefined,
  category: ActionCategory,
  isPending: boolean,
  selectedCount?: number,
) => {
  if (isPending) return "Sending...";

  if (!actionType) return "Submit";

  if (category === "log") {
    return "Log Action";
  }

  switch (actionType) {
    case "contact_creator":
      return "Send Message on TikTok";
    case "demonetization_ticket":
      return "Send Ticket";
    case "takedown_request":
      return "Send Request";
    case "remove_from_collaboration":
      return `Remove Creator${selectedCount && selectedCount > 1 ? "s" : ""} from collaboration`;
    case "remove_from_ad_campaign":
      return "Remove from ad campaign";
    case "trigger_policy":
      return "Trigger Policy";
    default:
      return "Submit";
  }
};

export const ActionRecordDialog = ({
  videos,
  selection: selectionProp,
  selectedCount: selectedCountProp,
  onClose,
  preselectedAction,
  preselectedCategory,
}: ActionRecordDialogProps) => {
  const category: ActionCategory = preselectedCategory || "take";
  const { startTracking: startBulkTracking } = useBulkActionContext();

  const selection: VideoSelection = useMemo(
    () => selectionProp ?? { mode: "include", videoIds: videos.map((v) => v.videoId) },
    [selectionProp, videos],
  );

  const displayCount = selectedCountProp ?? videos.length;
  const videoIds = useMemo(() => videos.map((v) => v.videoId), [videos]);
  const isBulk = displayCount > 1;

  const [selectedAction, setSelectedAction] = useState<ActionType | undefined>(preselectedAction);
  const [selectedCollaborationIds, setSelectedCollaborationIds] = useState<string[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<{ advertiserId: string; campaignId: string }[]>([]);
  const [autoApproveChecked, setAutoApproveChecked] = useState(false);
  const [showAutoApproveError, setShowAutoApproveError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const triggerPolicyEnabled = useFeatureFlag("F-trigger-policy");
  const { data: adsConnectionStatus } = useTikTokAdsConnectionStatus();
  const adsConnected = adsConnectionStatus?.connected === true;
  const filteredTakeActionTypes: readonly UiTakeActionType[] = (() => {
    const base: UiTakeActionType[] = [...MANUAL_TAKE_ACTION_TYPES];
    if (triggerPolicyEnabled) base.push("trigger_policy");
    return base;
  })();

  const uniqueCreators = useMemo(
    () =>
      Array.from(
        new Map(
          videos
            .filter((v): v is Video & { creatorId: string } => v.creatorId !== null)
            .map((v) => [
              v.creatorId,
              { creatorId: v.creatorId, handle: null as string | null, thumbnail: v.thumbnail },
            ]),
        ).values(),
      ),
    [videos],
  );

  const form = useForm<ActionFormValues>({
    defaultValues: {
      actionType: preselectedAction || "",
      notes: "",
      message: "",
      groupRequests: "grouped",
      policyId: "",
    },
  });

  useEffect(() => {
    form.clearErrors();
  }, [selectedAction, form]);

  const { mutateAsync: takeAction, isPending: isTakePending } = useTakeAction();
  const [messageImage, setMessageImage] = useState<ComposerImage | null>(null);
  const { store: storeImage, isPending: isStoringImage } = useStoreComposerImage();
  const { mutateAsync: logAction, isPending: isLogPending } = useLogAction();

  const collaborationSelection = selectedAction === "remove_from_collaboration" ? selection : null;
  const { data: collaborationInfo } = useVideoCollaborations(collaborationSelection);
  const { data: unreviewedData } = useUnreviewedCount(selection);
  const isPending = isTakePending || isLogPending || isStoringImage;

  useEffect(() => {
    if (preselectedAction) {
      setSelectedAction(preselectedAction);
      form.setValue("actionType", preselectedAction);
    }
  }, [preselectedAction, form]);

  useEffect(() => {
    if (selectedAction) {
      form.clearErrors();
      setSelectedCollaborationIds([]);
      setSelectedCampaignIds([]);
      setAutoApproveChecked(false);
      setShowAutoApproveError(false);
    }
  }, [selectedAction]);

  const handleClose = () => {
    form.reset();
    setSelectedAction(undefined);
    setSelectedCollaborationIds([]);
    setSelectedCampaignIds([]);
    setShowAutoApproveError(false);
    releaseComposerImage(messageImage);
    setMessageImage(null);
    onClose();
  };

  const onSubmit = async (payload: ActionFormValues) => {
    // Validate action type is selected
    if (!payload.actionType) {
      form.setError("actionType", { message: "Please select an action" });
      return;
    }

    const hasUnreviewedRisks = !!unreviewedData && unreviewedData.unreviewedCount > 0 && !autoApproveChecked;

    if (category === "take") {
      if (selectedAction === "contact_creator") {
        const missingMessage = !payload.message.trim();
        if (missingMessage) form.setError("message", { message: "Message is required" });
        if (hasUnreviewedRisks)
          form.setError("root", { message: "Please select checkbox to send message or review risks before sending" });
        if (missingMessage || hasUnreviewedRisks) return;
      }
      if ((selectedAction === "demonetization_ticket" || selectedAction === "takedown_request") && !payload.notes) {
        form.setError("notes", { message: "Reason is required" });
        return;
      }
      if (selectedAction === "trigger_policy" && !payload.policyId) {
        form.setError("policyId", { message: "Please select a policy" });
        return;
      }
      if (hasUnreviewedRisks && selectedAction !== "remove_from_collaboration") {
        form.setError("root", { message: "Please select checkbox to send message or review risks before sending" });
        return;
      }

      let openCollabIds: string[] | undefined;
      let targetCollabIds: string[] | undefined;
      if (selectedAction === "remove_from_collaboration") {
        if (selectedCollaborationIds.length === 0 || !collaborationInfo) return;

        const hasUnreviewedRisksForRemoval =
          !!unreviewedData && unreviewedData.unreviewedCount > 0 && !autoApproveChecked;
        if (hasUnreviewedRisksForRemoval) {
          setShowAutoApproveError(true);
          return;
        }

        openCollabIds = selectedCollaborationIds.filter((id) =>
          collaborationInfo.openCollaborations.some((c) => c.collaborationId === id),
        );
        targetCollabIds = selectedCollaborationIds.filter((id) =>
          collaborationInfo.targetCollaborations.some((c) => c.collaborationId === id),
        );
      }

      let imageId: string | null = null;
      if (selectedAction === "contact_creator") {
        try {
          imageId = await storeImage(messageImage);
        } catch {
          toastImageUploadFailed();
          return;
        }
      }

      const result = await takeAction({
        actionType:
          selectedAction === "trigger_policy" ? "trigger_sequence" : (payload.actionType as ApiTakeActionType),
        selection,
        notes: payload.notes || payload.message || "",
        groupTickets: isBulk ? payload.groupRequests === "grouped" : false,
        openCollaborationIds: openCollabIds,
        targetCollaborationIds: targetCollabIds,
        campaignIds: selectedAction === "remove_from_ad_campaign" ? selectedCampaignIds : undefined,
        policyId: payload.policyId,
        imageId: imageId ?? undefined,
      });

      if (result.async) {
        startBulkTracking(result.jobId, selectedAction as ActionType, selection);
        handleClose();
        return;
      }

      // Synchronous partial failure: some videos succeeded and some failed (e.g. a
      // remove_from_ad_campaign run where TikTok rejected part of the batch). Report it honestly
      // instead of a plain success toast so the count matches what actually changed (UNB-2024).
      if (result.failedCount > 0) {
        warning(`"${getTakeActionLabel(payload.actionType as UiTakeActionType)}" partially completed`, {
          description: `Completed for ${pluralize(result.count, "video", "videos")}; ${pluralize(result.failedCount, "video", "videos")} couldn't be processed.`,
        });
        handleClose();
        return;
      }

      if (!isBulk) {
        success("Success", {
          description:
            selectedAction === "contact_creator"
              ? `Risks approved and message sent to ${pluralize(uniqueCreators.length, "creator", "creators")}`
              : "Action completed successfully",
        });
      } else {
        success(
          selectedAction === "contact_creator"
            ? `Risks approved and message sent to ${pluralize(uniqueCreators.length, "creator", "creators")}`
            : `"${getTakeActionLabel(payload.actionType as UiTakeActionType)}" completed for ${pluralize(displayCount, "video", "videos")}`,
        );
      }
    } else {
      if (hasUnreviewedRisks) {
        form.setError("root", { message: "Please select checkbox to send message or review risks before sending" });
        return;
      }

      await logAction({
        actionType: payload.actionType as LogActionType,
        selection,
        notes: payload.notes || "",
      });

      if (!isBulk) {
        success("Success", {
          description: "Action logged successfully",
        });
      } else {
        success(
          `"${getLogActionLabel(payload.actionType as LogActionType)}" logged for ${pluralize(displayCount, "video", "videos")}`,
        );
      }
    }

    handleClose();
  };

  const renderFormFields = () => {
    if (category === "take") {
      if (!selectedAction) return null;

      switch (selectedAction) {
        case "contact_creator":
          return (
            <MessageCreatorFormFields
              form={form}
              disabled={isPending}
              videoCount={displayCount}
              creators={uniqueCreators}
              videoIds={videoIds}
              image={messageImage}
              onImageChange={setMessageImage}
              onPreviewChange={setShowPreview}
            />
          );
        case "remove_from_collaboration":
          return (
            <RemoveFromCollaborationFormFields
              videoSelection={selection}
              videoProductIds={videos.flatMap((v) => v.productList ?? [])}
              creators={uniqueCreators}
              disabled={isPending}
              selectedCollaborationIds={selectedCollaborationIds}
              onSelectionChange={setSelectedCollaborationIds}
              autoApproveChecked={autoApproveChecked}
              onAutoApproveChange={(checked) => {
                setAutoApproveChecked(checked);
                if (checked) setShowAutoApproveError(false);
              }}
              showAutoApproveError={showAutoApproveError}
            />
          );
        case "remove_from_ad_campaign":
          return (
            <RemoveFromAdCampaignFormFields
              form={form}
              disabled={isPending}
              selection={selection}
              videoCount={displayCount}
              selectedCampaignIds={selectedCampaignIds}
              onSelectionChange={setSelectedCampaignIds}
            />
          );
        case "demonetization_ticket":
          return (
            <TicketFormFields
              form={form}
              disabled={isPending}
              videoCount={displayCount}
              ticketType="demonetization"
              videos={videos.filter((v) => videoIds.includes(v.videoId))}
            />
          );
        case "takedown_request":
          return (
            <TicketFormFields
              form={form}
              disabled={isPending}
              videoCount={displayCount}
              ticketType="takedown"
              videos={videos.filter((v) => videoIds.includes(v.videoId))}
            />
          );
        case "trigger_policy":
          return <TriggerPolicyFormFields form={form} disabled={isPending} />;
        default:
          return <NotesFormFields form={form} disabled={isPending} />;
      }
    } else {
      return <NotesFormFields form={form} disabled={isPending} />;
    }
  };

  const PlaceholderIcon = category === "take" ? CursorBox : Transcript;
  const placeholderText = category === "take" ? "Select Action to take" : "Select Action to log";

  const isRemoveFromAdCampaign = selectedAction === "remove_from_ad_campaign";
  const isDemonetisationTicket = selectedAction === "demonetization_ticket";
  const hideActionSelector = isRemoveFromAdCampaign || isDemonetisationTicket;

  const getDialogTitle = () => {
    if (isRemoveFromAdCampaign) return "Add/ Remove from Ad campaign";
    if (isDemonetisationTicket) return "Send demonetisation ticket to TikTok";
    return category === "take" ? "Take Action" : "Log Action";
  };
  const dialogTitle = getDialogTitle();

  return (
    <ScrollableDialogContent className="sm:max-w-[600px]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 min-w-0">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          {!hideActionSelector && (
            <FormField
              control={form.control}
              name="actionType"
              render={({ field }) => {
                const Icon = field.value ? getActionTypeIcon(field.value as ActionType) : null;
                return (
                  <FormItem>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSelectedAction(value as ActionType);
                      }}
                      value={field.value}
                      disabled={isPending}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              <div className="flex items-center gap-2 text-quaternary">
                                <PlaceholderIcon className="w-5 h-5" />
                                <span>{placeholderText}</span>
                              </div>
                            }
                          >
                            {field.value && Icon && (
                              <div className="flex items-center gap-2">
                                <Icon className="w-5 h-5 text-tertiary" />
                                <span>
                                  {category === "take"
                                    ? getTakeActionLabel(field.value as UiTakeActionType)
                                    : getLogActionLabel(field.value as LogActionType)}
                                </span>
                              </div>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {category === "take"
                          ? filteredTakeActionTypes.map((actionType) => {
                              const ActionIcon = getActionTypeIcon(actionType);
                              // Must not be a SelectItem, or it becomes selectable.
                              if (actionType === "remove_from_ad_campaign" && !adsConnected) {
                                return (
                                  <Tooltip key={actionType} title={ADS_NOT_CONNECTED_TOOLTIP} placement="left">
                                    <Focusable>
                                      <div
                                        aria-disabled
                                        className="relative flex w-full items-center rounded-sm py-1.5 pl-8 pr-2 text-sm opacity-50 cursor-not-allowed select-none outline-none"
                                      >
                                        <div className="flex items-center gap-2">
                                          <ActionIcon className="w-5 h-5 text-tertiary" />
                                          <span>{getTakeActionLabel(actionType)}</span>
                                        </div>
                                      </div>
                                    </Focusable>
                                  </Tooltip>
                                );
                              }
                              return (
                                <SelectItem key={actionType} value={actionType}>
                                  <div className="flex items-center gap-2">
                                    <ActionIcon className="w-5 h-5 text-tertiary" />
                                    <span>{getTakeActionLabel(actionType)}</span>
                                  </div>
                                </SelectItem>
                              );
                            })
                          : LOG_ACTION_TYPES.map((actionType) => {
                              const ActionIcon = getActionTypeIcon(actionType);
                              return (
                                <SelectItem key={actionType} value={actionType}>
                                  <div className="flex items-center gap-2">
                                    <ActionIcon className="w-5 h-5 text-tertiary" />
                                    <span>{getLogActionLabel(actionType)}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          {renderFormFields()}

          {selectedAction !== "remove_from_collaboration" && unreviewedData && unreviewedData.unreviewedCount > 0 && (
            <div className="flex flex-col gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="auto-approve"
                  checked={autoApproveChecked}
                  onCheckedChange={(v) => setAutoApproveChecked(v === true)}
                  className={form.formState.errors.root && !autoApproveChecked ? "border-destructive border-2" : ""}
                />
                <label htmlFor="auto-approve" className="flex-1 text-sm font-medium text-primary">
                  Auto approve {pluralize(unreviewedData.unreviewedCount, "risk", "risks")} (
                  {pluralize(unreviewedData.unreviewedVideoCount, "video", "videos")}) without reviewing
                </label>
                <Tooltip
                  title="Some of these videos have risks which have not been reviewed. Skipping the review means creators may be notified for risks that don't exist. If incorrect approving this can also reduce the accuracy of future risk detection over time."
                  placement="top"
                >
                  <TooltipTrigger>
                    <InfoCircle className="w-5 h-5 text-tertiary" />
                  </TooltipTrigger>
                </Tooltip>
              </div>
              {form.formState.errors.root && !autoApproveChecked && (
                <p className="text-sm text-destructive pl-7">{form.formState.errors.root.message}</p>
              )}
            </div>
          )}

          <DialogFooter className={`items-center ${selectedAction === "contact_creator" ? "sm:justify-between" : ""}`}>
            {selectedAction === "contact_creator" && <SecurityInfoBox />}
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button type="button" color="secondary" disabled={isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                isDisabled={
                  isPending ||
                  showPreview ||
                  (category === "take" && !selectedAction) ||
                  (selectedAction === "remove_from_collaboration" && selectedCollaborationIds.length === 0) ||
                  (selectedAction === "remove_from_ad_campaign" && selectedCampaignIds.length === 0) ||
                  (category === "take" && selectedAction === "trigger_policy" && !form.watch("policyId"))
                }
              >
                {getButtonText(
                  selectedAction,
                  category,
                  isPending,
                  selectedCollaborationIds.length || uniqueCreators.length,
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </ScrollableDialogContent>
  );
};
